import { proxiedGet, proxiedPost, proxiedPut, getMemberCalls2Url } from '@/core/http'
import type {
    TaiwanCreateAccountPayload,
    TaiwanShippingPayload,
    TaiwanPeriodResponse,
    TaiwanFormatResponse,
    TaiwanHostedPaymentRequest,
    TaiwanHostedPaymentResponse,
    TaiwanDuplicateOrderResponse
} from '../types/taiwan.types'

const ALPHA3 = 'TWN'
const SYSTEM = 'AO'

// Trim string fields before sending so stray leading/trailing whitespace from
// form inputs never reaches the backend. `password` is preserved verbatim
// (spaces there could be intentional).
const TRIM_SKIP = ['password']
const trimValues = <T extends object>(body: T): T => {
    const out: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(body)) {
        out[key] = typeof value === 'string' && !TRIM_SKIP.includes(key) ? value.trim() : value
    }
    return out as T
}

// Source metadata attached to the address *create* (POST) — mirrors the legacy
// CreateUUID body (medium/agent + OS name from bowser). The address *update*
// (PUT) omits these (see fg-put.txt).
const getOSName = (): string => {
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
    if (/Windows/i.test(ua)) return 'Windows'
    if (/Android/i.test(ua)) return 'Android' // before Linux — Android UA contains "Linux"
    if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS'
    if (/Mac OS X|Macintosh/i.test(ua)) return 'macOS'
    if (/CrOS/i.test(ua)) return 'Chrome OS'
    if (/Linux|X11/i.test(ua)) return 'Linux'
    return 'Windows'
}
const sourceMeta = () => ({ medium: 'Internet', agent: 'uFeelGreat', platform: getOSName() })

// ─── URL builders (TW-specific — payment/checkout endpoints live in the plugin) ─
const formatDetailUrl = (uuid: string) => getMemberCalls2Url(`hot/format/${ALPHA3}/feelGreat/detail-v2/${uuid}?ordercalC=1&expandGenBeta=1`)

const formatAddressUrl = (queryString = '') => getMemberCalls2Url(`hot/formatV2/${ALPHA3}/feelGreat/address${queryString}`)

const createAccountUrl = (queryString = '') => getMemberCalls2Url(`hot/formatV2/${ALPHA3}/feelGreat/createAccount${queryString}`)

const periodUrl = () => getMemberCalls2Url('topup/period')

// Hosted-gateway prepare (proxy/insert) + duplicate-order guard live in the plugin.
const hostedPaymentUrl = () => getMemberCalls2Url(`hot/payment_router/proxy/insert/${ALPHA3}`)

const duplicateOrderUrl = (uuid: string) => getMemberCalls2Url(`hot/payment_router/duplicateOrder/${ALPHA3}/feelGreat/${uuid}`)

// ─── Entry period (join period, YYYYMM) ──────────────────────────────────────
export const getTaiwanPeriod = async (): Promise<string> => {
    const data = await proxiedPost<TaiwanPeriodResponse>(periodUrl(), {
        country: 'TW',
        system: SYSTEM,
        joinPeriod: ''
    })
    return data.entryPeriod
}

// ─── Create checkout UUID (guest enroll starts a format session) ──────────────
export interface TaiwanCreateUuidBody {
    type: 'enroll' | 'shop'
    period: string
    shipment_packs: string
    referral_id?: string
}

export const createTaiwanUuid = async (body: TaiwanCreateUuidBody): Promise<TaiwanFormatResponse> =>
    proxiedPost<TaiwanFormatResponse>(formatAddressUrl(), trimValues({ ...body, ...sourceMeta() }))

// ─── Fetch the order format/state for a UUID ──────────────────────────────────
export const getTaiwanFormat = async (uuid: string): Promise<Record<string, unknown>> => proxiedGet<Record<string, unknown>>(formatDetailUrl(uuid))

// ─── Guest enrollment (create account) ────────────────────────────────────────
export const createTaiwanAccount = async (
    payload: TaiwanCreateAccountPayload,
    opts: { isDev?: boolean; skipPasswordCheck?: boolean } = {}
): Promise<TaiwanFormatResponse> => {
    const params = new URLSearchParams()
    if (opts.isDev) params.set('dev', '1')
    else params.set('emailDelay', '1h')
    if (opts.skipPasswordCheck) params.set('isSkipCheckPassword', '1')
    const qs = params.toString()
    return proxiedPost<TaiwanFormatResponse>(createAccountUrl(qs ? `?${qs}` : ''), trimValues(payload))
}

// ─── Member shop session / shipping (POST/PUT /address, type=shop + login) ─────
// A logged-in member calls the /address endpoint with type=shop and their login
// identity; the server resolves the address from the member profile. Payload is
// exactly the fg-put.txt shape — no medium/agent/platform/period extras.
export const postTaiwanShipping = async (payload: TaiwanShippingPayload): Promise<TaiwanFormatResponse> => {
    // uuid exists → update via PUT with the exact fg-put.txt shape (no source
    // metadata). No uuid → create via POST with period + source metadata.
    return payload.uuid
        ? proxiedPut<TaiwanFormatResponse>(formatAddressUrl(), trimValues(payload))
        : proxiedPost<TaiwanFormatResponse>(formatAddressUrl(), trimValues({ ...payload, ...sourceMeta() }))
}

// ─── Record the chosen payment option on the format (PUT) ─────────────────────
// Mirrors legacy putHotFormatV2: before preparing a Cash-on-Delivery order, tag
// the format with payment_options=cashondelivery so the order settles as COD.
export const putTaiwanPaymentOption = async (uuid: string, shipmentPacks: string, paymentOption: string): Promise<TaiwanFormatResponse> =>
    proxiedPut<TaiwanFormatResponse>(formatAddressUrl(), trimValues({ uuid, payment_options: paymentOption, shipment_packs: shipmentPacks }))

// ─── Hosted-gateway / router payment (POST proxy/insert) ──────────────────────
// Credit card: no `payments` → the router returns a 2c2p redirect the FE follows.
// Cash on Delivery: `payments:[{method:'cashOnDelivery'}]` → the router settles
// the order directly and returns a referenceNo (no card redirect).
export const createTaiwanHostedPayment = async (body: TaiwanHostedPaymentRequest): Promise<TaiwanHostedPaymentResponse> =>
    proxiedPost<TaiwanHostedPaymentResponse>(hostedPaymentUrl(), body)

// ─── Duplicate-order guard (before preparing payment) ─────────────────────────
export const checkTaiwanDuplicateOrder = async (uuid: string): Promise<TaiwanDuplicateOrderResponse> =>
    proxiedGet<TaiwanDuplicateOrderResponse>(duplicateOrderUrl(uuid))
